// Users.js

import { nowTimestamp } from 'services/nodes/NodeWriteService';

// Utilities
import { PlanType } from 'utilities/Enums';
import { getPersonCoverUrl, getPersonName, getPersonPhoto } from 'utilities/PersonIdentity';

// Default user structure for new users. 
// 
// The key will be added when creating the user.
//
const defaultUser = {
    balance: 0,
    pendingBalance: 0,
    planKey: PlanType.FREE,
    likeCount: 0,
    location: {
        north: 0,
        south: 0,
        east: 0,
        west: 0,
    },
    gifts: [],
    darkMode: true,
    streamingUsed: 0,
};

export const ProfileTab = {
    NODES: 'NODES',
    FAVORITES: 'FAVORITES',
    LIKES: 'LIKES',
}

/**
 * Returns a new node object with default values, customized by type.
 */
export function getDefaultUser(uid, signupName, signupEmail) {

    const now = nowTimestamp();

    return {
        ...defaultUser,
        key: uid,
        name: signupName,
        email: signupEmail.toLowerCase(),
        emailVerified: false,
        dateCreated: now,
        dateModified: now,
    };
}

/**
 * Returns a flat summary of user information for display purposes.
 */
export function buildUserSummary(node) {
    if (!node) return null;

    const dataProfile = node?.data_profile || {};
    const key = node?.key || node?.userKey || dataProfile?.key || null;
    const coverUrl = getPersonCoverUrl(node) || null;
    const coverStoragePath = node?.coverStoragePath
        || dataProfile?.coverStoragePath
        || (
            typeof coverUrl === 'string'
            && coverUrl
            && !coverUrl.includes('://')
            && !coverUrl.startsWith('blob:')
            && !coverUrl.startsWith('data:')
                ? coverUrl
                : null
        );

    return {
        // From data_profile
        name: getPersonName(node) || dataProfile?.name || null,
        email: dataProfile?.email || node?.email || null,
        bio: dataProfile?.bio || null,

        // Not from data_profile
        key,
        userKey: node?.userKey || key || null,
        user: node?.user || null,
        type: node?.type || 'USER',
        title: node?.title || getPersonName(node) || null,
        coverUrl,
        coverStoragePath: coverStoragePath || null,
        url: node?.url || null,
        url_thumb: node?.url_thumb || null,
        storagePath: node?.storagePath || coverStoragePath || null,
    };
}

// Build a self profile node for SELECTED permissions and similar contexts.
// This matches the shape used in permission UIs elsewhere in the app.
export function buildSelfProfile(user) {
    if (!user?.key) return null;

    const profileName = getPersonName(user);
    const profilePhoto = getPersonPhoto(user) || null;
    const profileCoverUrl = getPersonCoverUrl(user) || null;
    const profileCoverStoragePath = user?.coverStoragePath || null;

    return {
        key: user.key,
        type: 'USER',
        userKey: user.key,
        title: profileName,
        coverUrl: profileCoverUrl,
        coverStoragePath: profileCoverStoragePath,
        data_profile: {
            name: profileName,
            photo: profilePhoto,
            coverUrl: profileCoverUrl,
            coverStoragePath: profileCoverStoragePath,
        },
    };
}

export function getUsersPhraseWithNames(names, defaultPhrase, user) {

    const selfName = user?.data_profile?.name || '';

    // Self-referenced notifications
    if (selfName && names.length === 1 &&
        names.includes(selfName)) {
        return `You`;
    }

    // Handle different cases based on array length
    if (names.length === 0 && defaultPhrase) return defaultPhrase;
    if (names.length === 1) return `${names[0]}`;
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
    return `${names[0]}, ${names[1]} and ${names.length - 2} others`;
}

export function getUsersPhrase(users, defaultPhrase, user) {

    // Extract display names from the users array
    const names = users.map((person) => {
        if (person?.data_profile?.name) return person.data_profile.name;
        if (person?.name) return person.name;
        return person?.key;
    });

    return getUsersPhraseWithNames(names, defaultPhrase, user);
}

export const getPossessiveName = (name) => {
    if (!name) return "Perspective";
    // Handle names ending in "s" (e.g., "James" → "James'")
    return name.endsWith('s') ? `${name}'` : `${name}'s`;
};